import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import {
  Control,
  DeepPartial,
  FieldValues,
  Path,
  useForm
} from "react-hook-form";

import useLockedFilterValues from "features/reports/hooks/use-locked-filter-values.hook";

import { CustomFilterOption } from "../custom-filters-list.component";

export type FilterOption<T extends FieldValues, K> = {
  name: string;
  label: string;
  editComponent: (
    name: string,
    setMetadata: (metadata: any) => void,
    control: Control<T>,
    updateFilter: (value: K[keyof K] | null) => void
  ) => React.ReactNode;
  renderViewMode: (filterKey: string, metadata: any) => React.ReactNode;
  onDelete?: () => void;
  /** See `CustomFilterOption.helpAdornment`. */
  helpAdornment?: React.ReactNode;
};

type UseCustomFiltersProps<T extends FieldValues, K> = {
  updateCustomFilter: (
    filterName: keyof K,
    filterValue: K[keyof K] | undefined | null
  ) => void;
  filterOptions: FilterOption<T, K>[];
  selectedCustomFilters: T;
  setSelectedCustomFilters: React.Dispatch<React.SetStateAction<T>>;
};

export default function useCustomFilters<T extends FieldValues, K>({
  updateCustomFilter,
  filterOptions,
  selectedCustomFilters,
  setSelectedCustomFilters
}: UseCustomFiltersProps<T, K>) {
  const defaultValues = useMemo(() => {
    const initialValues = filterOptions.reduce((acc, filter) => {
      acc[filter.name as keyof T] =
        selectedCustomFilters[filter.name as keyof T] || undefined;
      return acc;
    }, {} as Partial<T>);

    return initialValues as DeepPartial<T>;
  }, [selectedCustomFilters, filterOptions]);

  const formMethods = useForm<T>({
    mode: "onChange",
    defaultValues: defaultValues as any
  });

  const { control, reset, setValue } = formMethods;

  useEffect(() => {
    reset(defaultValues as T);
  }, [defaultValues, reset]);

  // ENG-1595: when a report is scoped to the user's own records (or to specific clinics), the
  // locked value has to reach the request params, not just gray out the control — an untouched
  // filter would otherwise still ask for everything. Seeded into the same state the list hook
  // reads, so the list request and the CSV export both inherit it.
  //
  // Seeding is keyed on the lock's signature, the same guard CustomFiltersList uses: it applies
  // when the lock first resolves, again on a report with a different lock, and again when the
  // option labels arrive (params carry the ids before the labels' fetch resolves). Per key:
  // a pinned filter (self-only staff, single-clinic scope) always takes the locked value; a
  // multi-clinic scope keeps an existing choice that stays within the allowed set (a saved view,
  // or a pick made before the labels resolved) and clamps anything outside it.
  const lockedValues = useLockedFilterValues();
  const seededSignatureRef = useRef("");

  useEffect(() => {
    const names = filterOptions.map((option) => option.name);
    const seeded = Object.keys(lockedValues.params).filter((name) =>
      names.includes(name)
    );

    if (!seeded.length) return;

    const signature = JSON.stringify([
      lockedValues.params,
      lockedValues.options
    ]);
    if (signature === seededSignatureRef.current) return;
    seededSignatureRef.current = signature;

    const optionSeeds: Record<string, unknown> = {};
    seeded.forEach((name) => {
      const current = selectedCustomFilters[name as keyof T];
      const currentOptions = (
        Array.isArray(current) ? current : current ? [current] : []
      ) as { value?: unknown }[];

      if (!lockedValues.pinnedKeys.has(name) && currentOptions.length) {
        const allowed = new Set(lockedValues.params[name].split(","));
        const inScope = currentOptions.filter((option) =>
          allowed.has(String(option?.value))
        );
        if (inScope.length === currentOptions.length) {
          // A narrower choice fully within the scope stands; its own edit path already sent it.
          return;
        }
        if (inScope.length) {
          // Partially out of scope (a stale saved view): clamp to what the scope allows.
          optionSeeds[name] = inScope;
          updateCustomFilter(
            name as keyof K,
            inScope.map((option) => String(option?.value)).join(",") as any
          );
          setValue(name as Path<T>, inScope as any);
          return;
        }
      }

      // Empty, pinned, or entirely out of scope: take the locked value. The request params want
      // the scalar the filter's own edit path would have sent; the picker and the pill want the
      // option objects, which wait on their labels.
      updateCustomFilter(name as keyof K, lockedValues.params[name] as any);
      if (lockedValues.options[name]) {
        optionSeeds[name] = lockedValues.options[name];
        setValue(name as Path<T>, lockedValues.options[name] as any);
      }
    });

    if (Object.keys(optionSeeds).length) {
      setSelectedCustomFilters((prev) => ({ ...prev, ...optionSeeds }) as T);
    }
  }, [
    lockedValues,
    filterOptions,
    selectedCustomFilters,
    setValue,
    updateCustomFilter,
    setSelectedCustomFilters
  ]);

  const filters: CustomFilterOption[] = useMemo(() => {
    return filterOptions.map((option) => {
      const customFilterOption: CustomFilterOption = {
        label: option.label,
        filterKey: option.name,
        helpAdornment: option.helpAdornment,
        renderViewMode: option.renderViewMode,
        editComponent: (filterKey, setMetadata) =>
          option.editComponent(
            filterKey,
            (metadata) => {
              setValue(filterKey as Path<T>, metadata as any);
              setSelectedCustomFilters((prev) => ({
                ...prev,
                [filterKey]: metadata
              }));
              setMetadata(metadata);
            },
            control,
            (value) => updateCustomFilter(filterKey as keyof K, value)
          ),
        onDelete: () => {
          setValue(option.name as Path<T>, null as any);
          setSelectedCustomFilters((prev) => ({
            ...prev,
            [option.name as Path<T>]: null
          }));
          updateCustomFilter(option.name as keyof K, undefined);
          option.onDelete?.();
        }
      };
      return customFilterOption;
    });
  }, [
    control,
    setValue,
    updateCustomFilter,
    filterOptions,
    setSelectedCustomFilters
  ]);

  return filters;
}
